/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  FIND_FEATURE_ASSIGNABLE_ACCOUNTS,
  SortDirectionEnum,
} from '@gripp/shared-logic';
import { AssignmentListGrid, BaseColumnCheckbox, Colors } from '@gripp/shared-ui';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleProp, StyleSheet, Text } from 'react-native';

export type FeatureAccountAssignmentListProps = {
  featureId: string;
  maxHeight?: number;
  keyword?: string;
  accountAssignments: string[];
  onAccountAssignmentsChange: (assignments: string[]) => void;
};

export const FeatureAccountAssignmentList = ({
  featureId,
  maxHeight,
  keyword,
  accountAssignments,
  onAccountAssignmentsChange,
}: FeatureAccountAssignmentListProps) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<any>(() => {
    if (featureId) {
      return {
        keyword,
        featureId,
        assignments: accountAssignments,
      };
    }
    return undefined;
  });

  useEffect(() => {
    setFilter({
      keyword,
      featureId,
      assignments: accountAssignments,
    });
  }, [keyword, featureId]);

  const columnHelper = createColumnHelper<any>();

  const getLabel = (labelStyle: StyleProp<any>, label: string) => {
    return <Text style={[labelStyle, styles.cellText]}>{label}</Text>;
  };

  const columns = [
    columnHelper.accessor('selected', {
      cell: BaseColumnCheckbox.checkbox,
      header: BaseColumnCheckbox.checkboxHeader,
      meta: { style: styles.checkboxCell },
    }),
    columnHelper.accessor('name', {
      cell: (info) => getLabel(styles.nameLabel, info.getValue()),
      header: () => t('features.assignment.name'),
      meta: { style: styles.cell },
    }),
    columnHelper.accessor('email', {
      cell: (info) => getLabel(styles.greyLabel, info.getValue() || ''),
      header: () => t('features.assignment.email'),
      meta: { style: styles.cell },
    }),
    columnHelper.accessor('phone', {
      cell: (info) => getLabel(styles.greyLabel, info.getValue() || ''),
      header: () => t('features.assignment.phone'),
      meta: { style: styles.cell },
    }),
  ] as ColumnDef<any, any>[];

  if (!filter) return null;

  return (
    <AssignmentListGrid
      modelName="account"
      query={FIND_FEATURE_ASSIGNABLE_ACCOUNTS}
      dataPrefix="feature.findAssignableAccounts"
      columns={columns}
      filter={filter}
      assignments={accountAssignments}
      onAssignmentsChange={onAccountAssignmentsChange}
      maxHeight={maxHeight}
      textEmpty={t('features.assignment.noAccounts')}
      orderBy={{ field: 'name', order: SortDirectionEnum.Asc }}
    />
  );
};

const styles = StyleSheet.create({
  cell: {
    alignItems: 'center',
    justifyContent: 'flex-start',
    overflow: 'hidden',
    paddingVertical: 4,
    ...Platform.select({
      web: {
        height: 50,
        paddingRight: 16,
      },
      default: {
        height: 68,
        paddingRight: 12,
      },
    }),
  },
  checkboxCell: {
    maxWidth: 50,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  nameLabel: {
    color: Colors.black,
  },
  greyLabel: {
    color: Colors.grayText,
  },
  cellText: {
    fontSize: 16,
    lineHeight: 17,
    ...Platform.select({
      web: {
        fontWeight: '500',
        flexWrap: 'nowrap',
      },
      default: {
        fontWeight: '400',
        flexWrap: 'wrap',
      },
    }),
  },
});
